import {
  CreateZcodeRequest,
  UpdateZcodeSchema,
  ZCodeDto,
  ZcodeSchema,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useForm } from 'react-hook-form';
import {
  createNeedsMetadataSuccess,
  updateNeedsMetadataSuccess,
} from 'redux/slices/metaSlice';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { z } from 'zod';
import { useToast } from '../../../../app/ReusableToast';
import { AdminDrawerForm } from '../../AdminDrawer';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  isEdit: boolean;
  selectedZcode?: ZCodeDto;
}

export const NeedZcodeCreateEdit = ({
  open,
  setOpen,
  isEdit,
  selectedZcode,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const [needZcodeResult, createNeedZcodeLoading, createNeedZcode] = useApiCall(
    needRequests.createNeedsMetadata
  );
  const [updateNeedZcodeResult, updateNeedZcodeLoading, updateNeedZcode] =
    useApiCall(needRequests.updateNeedsMetadata);
  const schema = isEdit ? UpdateZcodeSchema : ZcodeSchema;
  type ZcodeFormData = z.infer<typeof schema>;
  const { control, handleSubmit, formState, watch, setValue } =
    useForm<ZcodeFormData>({
      resolver: zodResolver(schema),
      defaultValues: {
        label: selectedZcode?.label ?? '',
        key: selectedZcode?.key ?? '',
        overview: selectedZcode?.overview ?? '',
        issues:
          selectedZcode?.issues && selectedZcode.issues.length > 0
            ? selectedZcode.issues.join(',')
            : '',
        assistances:
          selectedZcode?.assistances && selectedZcode.assistances.length > 0
            ? selectedZcode.assistances.join(',')
            : '',
      },
    });

  const keyValue = watch('key');

  // Auto-format key to ensure it follows "zcode_Z##.#" format (any number of digits)
  useEffect(() => {
    if (keyValue && typeof keyValue === 'string' && keyValue.trim() !== '') {
      const trimmedKey = keyValue.trim().replace(/\s/g, '');

      // Extract the code part (everything after "zcode_" or just the input if no prefix)
      let codePart = trimmedKey;
      if (trimmedKey.startsWith('zcode_')) {
        codePart = trimmedKey.replace(/^zcode_/, '');
      }

      // Remove any existing "zcode_" prefix for processing (in case user typed it twice)
      codePart = codePart.replace(/^zcode_/, '');

      // Extract digits and format: Z##.# (any number of digits before and after dot)
      // Match pattern: optional Z, digits before dot, optional dot, digits after dot
      const match = codePart.match(/^Z?(\d*)\.?(\d*)$/);

      if (match) {
        const [, beforeDot, afterDot] = match;

        // Only format if we have at least one digit before the dot
        if (beforeDot && beforeDot.length > 0) {
          // Ensure we have at least one digit after the dot (add 0 if missing)
          const afterDotFormatted =
            afterDot && afterDot.length > 0 ? afterDot : '0';

          const formattedCode = `Z${beforeDot}.${afterDotFormatted}`;
          const formattedKey = `zcode_${formattedCode}`;

          // Only update if the formatted value is different to avoid infinite loops
          if (formattedKey !== keyValue) {
            setValue('key', formattedKey, { shouldValidate: true });
          }
        }
      }
    }
  }, [keyValue, setValue]);

  const formConfig: FormConfig<ZcodeFormData> = [
    {
      type: 'input',
      name: 'overview',
      label: 'Overview',
      input: 'text',
      props: {
        multiline: true,
      },
    },
    {
      type: 'input',
      name: 'issues',
      label: 'Issues',
      input: 'text',
      props: {
        multiline: true,
      },
    },
    {
      type: 'input',
      name: 'assistances',
      label: 'Assistances',
      input: 'text',
    },
    {
      type: 'input',
      name: 'key',
      label: 'Key',
      input: 'text',
    },
  ];

  const onSubmit = (data: ZcodeFormData) => {
    if (isEdit) {
      void updateNeedZcode({
        body: data,
        params: { type: 'zcodes', id: selectedZcode?.id },
      });
    } else {
      void createNeedZcode({
        body: data as CreateZcodeRequest,
        params: { type: 'zcodes' },
      });
    }
  };

  // Handle successful creation
  useEffect(() => {
    if (needZcodeResult && !createNeedZcodeLoading && open) {
      dispatch(
        createNeedsMetadataSuccess({
          data: needZcodeResult,
          type: 'zcodes',
        })
      );
      toast('Zcode created successfully');
      setOpen(false);
    }
  }, [needZcodeResult, createNeedZcodeLoading]);

  // Handle successful update
  useEffect(() => {
    if (updateNeedZcodeResult && !updateNeedZcodeLoading && open) {
      dispatch(
        updateNeedsMetadataSuccess({
          data: updateNeedZcodeResult,
          type: 'zcodes',
        })
      );
      toast('Zcode updated successfully');
      setOpen(false);
    }
  }, [updateNeedZcodeResult, updateNeedZcodeLoading]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(() => {
    return (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Zcode` : `Add New Zcode`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
